// src/gac/BlinkLedPanel.js
// Extracted from GlobalActionCenter.js (GAC modularization, phase 1). No behaviour change.
import React, { useState, useRef } from 'react';
import axios from '../axiosInstance';
import '../GlobalActionCenterStyles.css';

const BlinkLedPanel = ({
    globalCompany,
    globalSelectedStore,
    executing,
    setExecuting,
    results,
    setResults,
    setErrors,
}) => {
    // Blink LED specific state
    const [blinkLedConfig, setBlinkLedConfig] = useState({
        inputMethod: 'textarea', // 'textarea' or 'csv'
        labelCodes: '',
        ledColor: 'GREEN',
        duration: '30s',
        blinkType: 'label', // 'label' or 'article'
        isPartial: true // Continue on failure
    });
    const [showFailuresModal, setShowFailuresModal] = useState(false);
    const csvFileInputRef = useRef(null);

    // Duration options for LED blink
    const durationOptions = [
        { value: '0s', label: '0 seconds' },
        { value: '10s', label: '10 seconds' },
        { value: '30s', label: '30 seconds' },
        { value: '1m', label: '1 minute' },
        { value: '2m', label: '2 minutes' },
        { value: '5m', label: '5 minutes' },
        { value: '10m', label: '10 minutes' },
        { value: '15m', label: '15 minutes' },
        { value: '20m', label: '20 minutes' },
        { value: '30m', label: '30 minutes' },
        { value: '60m', label: '60 minutes' }
    ];

    // Handle CSV file upload for Blink LED
    const handleCsvUpload = (event) => {
        const file = event.target.files[0];
        if (!file) return;

        const reader = new FileReader();
        reader.onload = (e) => {
            const text = e.target.result;
            // Parse CSV - extract label codes from first column
            const lines = text.split(/\r?\n/);
            const labelCodes = [];

            for (const line of lines) {
                const trimmed = line.trim();
                if (!trimmed) continue;

                // Handle CSV with commas - take first column
                const firstCol = trimmed.split(',')[0].trim();
                // Remove quotes if present
                const cleanCode = firstCol.replace(/^["']|["']$/g, '').trim();

                if (cleanCode && cleanCode.length > 0) {
                    labelCodes.push(cleanCode);
                }
            }

            setBlinkLedConfig(prev => ({
                ...prev,
                labelCodes: labelCodes.join('\n')
            }));
        };
        reader.readAsText(file);

        // Reset file input
        event.target.value = '';
    };

    // Parse and validate IDs from textarea
    const parseIds = () => {
        const lines = blinkLedConfig.labelCodes.split(/\r?\n/);
        const ids = [];
        const invalid = [];

        for (const line of lines) {
            const trimmed = line.trim();
            if (!trimmed) continue;

            // Check alphanumeric only
            const isAlphanumeric = /^[A-Za-z0-9]+$/.test(trimmed);

            // For labels, check 12 character length
            const isValidLength = blinkLedConfig.blinkType === 'article' || trimmed.length === 12;

            if (isAlphanumeric && isValidLength) {
                ids.push({ id: trimmed, valid: true });
            } else {
                ids.push({
                    id: trimmed,
                    valid: false,
                    reason: !isAlphanumeric
                        ? 'Non-alphanumeric characters'
                        : `Invalid length (${trimmed.length}, must be 12)`
                });
                invalid.push(trimmed);
            }
        }

        return { all: ids, valid: ids.filter(i => i.valid).map(i => i.id), invalid };
    };

    // Execute Blink LED action. Server chunks into batches of 500 behind the scenes.
    const executeBlinkLed = async () => {
        if (!globalCompany) {
            alert('Please select a company first');
            setExecuting(false);
            return;
        }

        if (!globalSelectedStore) {
            alert('Please select a store first');
            setExecuting(false);
            return;
        }

        const { valid: validIds, all: allIds } = parseIds();

        if (validIds.length === 0) {
            alert('No valid IDs entered. Check that all entries are alphanumeric' +
                (blinkLedConfig.blinkType === 'label' ? ' and exactly 12 characters.' : '.'));
            setExecuting(false);
            return;
        }

        const MAX_IDS = 10000;
        if (validIds.length > MAX_IDS) {
            alert(`Too many IDs (${validIds.length.toLocaleString()}). Maximum is ${MAX_IDS.toLocaleString()} per request.`);
            setExecuting(false);
            return;
        }

        const typeLabel = blinkLedConfig.blinkType === 'article' ? 'articles' : 'labels';
        console.log(`[Blink LED] Starting blink for ${validIds.length} ${typeLabel} in ${globalCompany.companyCode}/${globalSelectedStore.store}`);

        const payload = {
            companyCode: globalCompany.companyCode,
            storeCode: globalSelectedStore.store,
            ids: validIds,
            ledColor: blinkLedConfig.ledColor,
            duration: blinkLedConfig.duration,
            blinkType: blinkLedConfig.blinkType,
            isPartial: blinkLedConfig.isPartial
        };

        try {
            const response = await axios.post('global-action/actions/blink-led', payload);

            setResults({
                summary: {
                    total_labels_requested: allIds.length,
                    successful: response.data.successful || 0,
                    failed: response.data.failed || 0,
                    message: response.data.message
                },
                details: response.data.details || null
            });

            if (!response.data.success) {
                setErrors([response.data.message || 'Blink LED request had failures']);
            }
        } catch (error) {
            const errorMsg = error.response?.data?.message || error.message;
            setErrors([`Blink LED failed: ${errorMsg}`]);
        }
    };

    // Same sequence the shell's executeAction dispatcher used for this action.
    const handleExecute = async () => {
        setExecuting(true);
        setResults(null);
        setErrors([]);
        try {
            await executeBlinkLed();
        } catch (error) {
            console.error('Error executing action:', error);
            setErrors(['Execution error: ' + (error.response?.data?.message || error.message)]);
        } finally {
            setExecuting(false);
        }
    };

    return (
        <>
            <div className="blink-led-container">
                {/* Left Panel - Configuration & Input */}
                <div className="action-config-section blink-led-section">
                    <h3>Blink LED Configuration</h3>
                    <div className="config-panel">
                        {/* Blink Type Radio */}
                        <div className="config-row">
                            <label>Blink By:</label>
                            <div className="blink-type-toggle">
                                <label className={`radio-label ${blinkLedConfig.blinkType === 'label' ? 'active' : ''}`}>
                                    <input
                                        type="radio"
                                        name="blinkType"
                                        value="label"
                                        checked={blinkLedConfig.blinkType === 'label'}
                                        onChange={() => setBlinkLedConfig(prev => ({ ...prev, blinkType: 'label', labelCodes: '' }))}
                                    />
                                    Label ID
                                </label>
                                <label className={`radio-label ${blinkLedConfig.blinkType === 'article' ? 'active' : ''}`}>
                                    <input
                                        type="radio"
                                        name="blinkType"
                                        value="article"
                                        checked={blinkLedConfig.blinkType === 'article'}
                                        onChange={() => setBlinkLedConfig(prev => ({ ...prev, blinkType: 'article', labelCodes: '' }))}
                                    />
                                    Product/Article ID
                                </label>
                            </div>
                        </div>

                        {/* LED Color & Duration Row */}
                        <div className="config-row config-row-inline">
                            <div className="config-field">
                                <label>LED Color:</label>
                                <select
                                    value={blinkLedConfig.ledColor}
                                    onChange={(e) => setBlinkLedConfig(prev => ({ ...prev, ledColor: e.target.value }))}
                                >
                                    <option value="BLUE">Blue</option>
                                    <option value="CYAN">Cyan</option>
                                    <option value="GREEN">Green</option>
                                    <option value="MAGENTA">Magenta</option>
                                    <option value="RED">Red</option>
                                    <option value="WHITE">White</option>
                                    <option value="YELLOW">Yellow</option>
                                </select>
                            </div>
                            <div className="config-field">
                                <label>Duration:</label>
                                <select
                                    value={blinkLedConfig.duration}
                                    onChange={(e) => setBlinkLedConfig(prev => ({ ...prev, duration: e.target.value }))}
                                >
                                    {durationOptions.map(opt => (
                                        <option key={opt.value} value={opt.value}>{opt.label}</option>
                                    ))}
                                </select>
                            </div>
                        </div>

                        {/* Input Method Toggle */}
                        <div className="config-row">
                            <label>Input Method:</label>
                            <div className="input-method-toggle">
                                <button
                                    className={`toggle-btn ${blinkLedConfig.inputMethod === 'textarea' ? 'active' : ''}`}
                                    onClick={() => setBlinkLedConfig(prev => ({ ...prev, inputMethod: 'textarea' }))}
                                >
                                    Text Input
                                </button>
                                <button
                                    className={`toggle-btn ${blinkLedConfig.inputMethod === 'csv' ? 'active' : ''}`}
                                    onClick={() => setBlinkLedConfig(prev => ({ ...prev, inputMethod: 'csv' }))}
                                >
                                    Upload CSV
                                </button>
                            </div>
                        </div>

                        {/* ID Input Area with Blink Button */}
                        <div className="config-row label-input-row">
                            <div className="label-input-header">
                                <label>{blinkLedConfig.blinkType === 'article' ? 'Product/Article IDs:' : 'Label Codes (12 chars each):'}</label>
                            </div>
                            <div className="label-input-area">
                                {blinkLedConfig.inputMethod === 'textarea' ? (
                                    <>
                                        <textarea
                                            className="label-codes-textarea"
                                            value={blinkLedConfig.labelCodes}
                                            onChange={(e) => setBlinkLedConfig(prev => ({ ...prev, labelCodes: e.target.value }))}
                                            placeholder={blinkLedConfig.blinkType === 'article'
                                                ? "Enter product/article IDs, one per line.\n\nExample:\nB100001\nB100002\nB100003"
                                                : "Enter label codes (12 chars), one per line.\n\nExample:\n0B8B56AC7291\n0B8B56AC7292\n0B8B56AC7293"
                                            }
                                            rows={10}
                                        />
                                        <div className="label-count-row">
                                            <div className="label-count-info">
                                                <span className="count-valid">{parseIds().valid.length.toLocaleString()} valid</span>
                                                {parseIds().invalid.length > 0 && (
                                                    <span className="count-invalid"> / {parseIds().invalid.length.toLocaleString()} invalid</span>
                                                )}
                                                <span className="count-max"> (max 10,000)</span>
                                            </div>
                                            <button
                                                className="execute-button blink-led-button"
                                                onClick={handleExecute}
                                                disabled={executing || !globalCompany || !globalSelectedStore || parseIds().valid.length === 0}
                                            >
                                                {executing ? 'Sending...' : `Blink ${parseIds().valid.length.toLocaleString()} ${blinkLedConfig.blinkType === 'article' ? 'Products' : 'Labels'}`}
                                            </button>
                                        </div>
                                        {/* Show invalid IDs */}
                                        {parseIds().all.filter(i => !i.valid).length > 0 && (
                                            <div className="invalid-ids-preview">
                                                <strong>Invalid entries:</strong>
                                                {parseIds().all.filter(i => !i.valid).slice(0, 5).map((item, idx) => (
                                                    <span key={idx} className="invalid-id">{item.id} <small>({item.reason})</small></span>
                                                ))}
                                                {parseIds().all.filter(i => !i.valid).length > 5 && (
                                                    <span className="invalid-more">...and {parseIds().all.filter(i => !i.valid).length - 5} more</span>
                                                )}
                                            </div>
                                        )}
                                    </>
                                ) : (
                                    <div className="csv-upload-area">
                                        <input
                                            type="file"
                                            ref={csvFileInputRef}
                                            accept=".csv,.txt"
                                            onChange={handleCsvUpload}
                                            style={{ display: 'none' }}
                                        />
                                        <button
                                            className="csv-upload-btn"
                                            onClick={() => csvFileInputRef.current?.click()}
                                        >
                                            Choose CSV File
                                        </button>
                                        <p className="csv-hint">CSV should have {blinkLedConfig.blinkType === 'article' ? 'article IDs' : 'label codes'} in the first column</p>
                                        {blinkLedConfig.labelCodes && (
                                            <div className="csv-loaded-info">
                                                Loaded {parseIds().valid.length.toLocaleString()} valid / {parseIds().invalid.length.toLocaleString()} invalid from file
                                            </div>
                                        )}
                                    </div>
                                )}
                            </div>
                        </div>
                    </div>
                </div>

                {/* Right Panel - Results */}
                <div className="action-config-section blink-led-results-section">
                    <h3>Results</h3>
                    <div className="config-panel">
                        {results && results.summary ? (
                            <>
                                <div className="results-summary blink-led-results">
                                    <div className="summary-card info">
                                        <div className="summary-label">{blinkLedConfig.blinkType === 'article' ? 'Articles' : 'Labels'} Requested</div>
                                        <div className="summary-value">{results.summary.total_labels_requested?.toLocaleString()}</div>
                                    </div>
                                    <div className="summary-card success">
                                        <div className="summary-label">Successful</div>
                                        <div className="summary-value">{results.summary.successful?.toLocaleString()}</div>
                                    </div>
                                    <div
                                        className={`summary-card ${results.summary.failed > 0 ? 'error clickable' : 'success'}`}
                                        onClick={() => results.summary.failed > 0 && setShowFailuresModal(true)}
                                        title={results.summary.failed > 0 ? 'Click to view failure details' : ''}
                                    >
                                        <div className="summary-label">Failed</div>
                                        <div className="summary-value">{results.summary.failed?.toLocaleString()}</div>
                                    </div>
                                </div>

                                {results.summary.message && (
                                    <div className={`blink-result-message ${results.summary.failed === 0 ? 'success' : 'warning'}`}>
                                        {results.summary.message}
                                    </div>
                                )}

                                {results.details && (
                                    <div className="blink-details">
                                        <p>
                                            <strong>Company:</strong> {results.details.company} |
                                            <strong> Store:</strong> {results.details.store} |
                                            <strong> Color:</strong> {results.details.color} |
                                            <strong> Duration:</strong> {results.details.duration}
                                        </p>
                                    </div>
                                )}
                            </>
                        ) : (
                            <div className="no-results-placeholder">
                                <p>Results will appear here after blinking {blinkLedConfig.blinkType === 'article' ? 'products' : 'labels'}.</p>
                            </div>
                        )}
                    </div>
                </div>
            </div>

            {/* Failures Modal */}
            {showFailuresModal && results?.details?.failures && (
                <div className="failures-modal-overlay" onClick={() => setShowFailuresModal(false)}>
                    <div className="failures-modal" onClick={e => e.stopPropagation()}>
                        <div className="failures-modal-header">
                            <h3>Failed {blinkLedConfig.blinkType === 'article' ? 'Articles' : 'Labels'}</h3>
                            <button className="modal-close-btn" onClick={() => setShowFailuresModal(false)}>×</button>
                        </div>
                        <div className="failures-modal-info">
                            <p><strong>Company:</strong> {results.details.company} | <strong>Store:</strong> {results.details.store}</p>
                        </div>
                        <div className="failures-modal-content">
                            <table className="failures-table">
                                <thead>
                                    <tr>
                                        <th>{blinkLedConfig.blinkType === 'article' ? 'Article ID' : 'Label Code'}</th>
                                        <th>Reason</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {results.details.failures.map((failure, idx) => (
                                        <tr key={idx}>
                                            <td className="failure-id">{failure.id}</td>
                                            <td className="failure-reason">{failure.reason}</td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>
                    </div>
                </div>
            )}
        </>
    );
};

export default BlinkLedPanel;
